import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, CircularProgress, ClickAwayListener, FormControlLabel, IconButton, Popover, Popper, Typography } from "@mui/material"
import CustomCard from "./CustomCard"
import luciLogo from "../../../assets/luciLogo2.png"
import poweredBy from "../../../assets/poweredBy.svg"
import { makeStyles } from "@mui/styles";
import { timeStampToDateTime, timeStampToDayString, timeStampToFormatTimeZone } from "../../../utils/commonServices";
import {
  formatDueDate,
  isOverdue,
  parseDueDate,
  computeCountdown,
  toEpochSeconds,
} from '../../../utils/dueDateUtils';
import DateTimePicker from '../../../components/date-time/DateTimePicker';
import "@fontsource/rubik";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSION } from "../../../constants/constants";
import { updateCreationState } from "../../../redux/eventCreationSlice";
import CustomButton from "../../../components/CustomButton";
import { updateState } from "../../../redux/commonSlice";
import CustomTooltip from "../../../components/CustomTooltip";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useCustomTheme from "../../../theme/useCustomTheme";
import { updateProjectState } from "../../../redux/projectSlice";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarIcon from '@mui/icons-material/Star';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AutoAssignedBadge from './AutoAssignedBadge';
import { updateDueDateApi } from '../../../services/api';
import { setSnackbar } from '../../../redux/messageSlice';
import { useQueryClient } from 'react-query';

const useStyles = makeStyles((theme) => ({
 fontWeightNormal:{
  fontWeight: 'normal'
 },
fontWeight:{
  fontWeight:600
},
rubik:{
  fontFamily:'Rubik'
}

}));

const EventDetailCard = (props) => {

  const commonReducer = useSelector((state)=> state.root.commonReducer)
  const themeReducer = useSelector((state) => state.root.themeReducer);
  const dropdownReducer = useSelector((state) => state.root.dropdownReducer);
  const projectReducer = useSelector((state)=> state.projectReducer)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [hasPermission] = usePermissions()
  const [theme ] = useCustomTheme()
  const [selectedVideos, setSelectedVideos] = useState([]);

  const {event} = props
  const [expanded, setExpanded] = useState(0);

  const handleAccordionChange = (panelIndex) => (_, isExpanded) => {
    setExpanded(isExpanded ? panelIndex : 0);
    setSelectedVideos([])
  };

  const handleVideoSelect = (videoId) => {
    setSelectedVideos((prevSelected) => {
      if (prevSelected.includes(videoId)) {
        return prevSelected.filter((id) => id !== videoId);
      } else if (prevSelected.length < 2) {
        return [...prevSelected, videoId];
      } else {
        return [prevSelected[1], videoId];
      }
    });
  };

  const brandLogo = themeReducer?.brand_logo
  const classes = useStyles();
  const isAdmin = commonReducer?.role_name === 'Admin';

  // Fall back to navigation-state due_date until backend returns it in project_details
  const dueDate = projectReducer?.project_details?.due_date ?? location?.state?.due_date;
  // Read from Redux — set on card click, cleared by resetProjectState when dashboard mounts
  const organisationName = projectReducer?.event_organisation_name ?? projectReducer?.project_details?.organisation_name;

  const dueDateStr = formatDueDate(dueDate);
  const overdue = isOverdue(dueDate);

  // ─── Countdown timer ───────────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(() => computeCountdown(dueDate));

  useEffect(() => {
    const update = () => setCountdown(computeCountdown(dueDate));
    update();
    if (!dueDate) return;
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [dueDate]);

  // ─── Due date edit picker ──────────────────────────────────────────────────
  const [dueDateAnchor, setDueDateAnchor] = useState(null);
  const [dueDatePickerDate, setDueDatePickerDate] = useState(null);
  const [savingDueDate, setSavingDueDate] = useState(false);

  const openDueDatePopover = (e) => {
    setDueDatePickerDate(dueDate ? parseDueDate(dueDate) : new Date());
    setDueDateAnchor(e.currentTarget);
  };
  const closeDueDatePopover = () => setDueDateAnchor(null);

  const saveDueDateHandler = async () => {
    if (!dueDatePickerDate) return;
    setSavingDueDate(true);
    const epoch = toEpochSeconds(dueDatePickerDate);
    try {
      await updateDueDateApi({ event_id: projectReducer?.project_details?.id, due_date: epoch });
      dispatch(setSnackbar({ flag: true, type: 'success', message: 'Due date updated successfully' }));
      queryClient.invalidateQueries(['kanban-board-summary']);
      queryClient.invalidateQueries(['kanban-column']);
      dispatch(updateProjectState({
        project_details: { ...projectReducer?.project_details, due_date: epoch }
      }));
      closeDueDatePopover();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ??
        (status === 422 ? 'Due date cannot be in the past'
          : status === 403 ? 'You do not have permission to set due dates'
          : 'Failed to update due date');
      dispatch(setSnackbar({ flag: true, type: 'error', message: msg }));
    } finally {
      setSavingDueDate(false);
    }
  };

  const setPageData = ()=> {
    dispatch(updateCreationState({
        pageData: {
            name : event?.name,
            start_date: event?.start_date,
            end_date: event?.end_date,
            editor_start_date: event?.editor_start_date,
            video_orientation: event?.video_orientation,
            description: event?.description,
            event_type_id: dropdownReducer.event_type.find((type)=> type.value == event.event_type)?.id,
            video_length_id: dropdownReducer.video_length.find((length)=> length.value == event.video_length)?.id,
        }
    }))
}

  const editProjectClickHandler =()=>{
    if(event?.status_type_order < 3){
        setPageData()
       dispatch(updateCreationState({open_form: true, edit: true, event_id: event?.id}))
      }else{
      dispatch(updateCreationState({edit_decline_popup:true}))
      }
  }

  const openDocumentClickHandler = async ()=>{
      const documentUrl = commonReducer?.event_details?.note_file_url;
      window.open(documentUrl, '_blank');
  }

  const countdownColor = countdown?.color === 'red' ? '#e53935' : countdown?.color === 'yellow' ? '#f9a825' : '#43a047';

  return (
    <CustomCard className='rubik' style={{ height:'100%', padding:0 , '& .MuiCardContent-root:last-child':{paddingBottom:0}, minWidth:'20vw'}} >
      <Box className="rubik" sx={{height:'100%', display:'flex', flexDirection:'column', paddingY:{xs:'24px',sm:'24px',md:'24px',lg:'30px',xl:'40px'} }}>

        <Box className="rubik" sx={{display:'felx', flexDirection:'column', fontSize: {xs:'10px',md:'12px',lg:'14px',xl:'16px'},  marginRight:{md:'10px',lg:'0.52vw',xl:'0.78vw'}}}>
          <Box mb={{xs:0.5,md:0.8,   xl:1.3}} sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, mt:3}}>
            <Typography sx={{fontWeight:'normal',  fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Lucivideo Name</Typography>
            <Typography sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px', wordWrap: 'break-word'}, textTransform:'capitalize', fontFamily:'Rubik'}}>{projectReducer?.project_details?.name}</Typography>
            {organisationName && (
              <Typography sx={{ fontSize: {xs:'8px',md:'8px',lg:'9.5px',xl:'13px'}, fontFamily:'Rubik', color:'#878887', mt: 0.25 }}>
                {organisationName}
              </Typography>
            )}
          </Box>
          <Box mb={{xs:0.5, md:0.8,  xl:1.3}} sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{color:'#878887' , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Status</Typography>
              <Typography sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>{projectReducer?.project_details?.status_type}</Typography>
            </Box>
            {(projectReducer?.event_tags?.includes('Client Revision') || projectReducer?.event_tags?.includes('Internal Revision')) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: 0.5 }}>
                {projectReducer?.event_tags?.includes('Client Revision') && (
                  <CustomTooltip title="Returned from Client Revision stage">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '4px', px: '5px', py: '1px' }}>
                      <ReplayRoundedIcon sx={{ fontSize: {xs:'8px !important', lg:'9px !important'}, color: '#B45309' }} />
                      <Typography sx={{ fontSize: {xs:'8px', lg:'9px'}, fontFamily: 'Rubik', fontWeight: 600, color: '#B45309', lineHeight: 1 }}>Client Rev.</Typography>
                    </Box>
                  </CustomTooltip>
                )}
                {projectReducer?.event_tags?.includes('Internal Revision') && (
                  <CustomTooltip title="Returned from Internal Revision stage">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', backgroundColor: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: '4px', px: '5px', py: '1px' }}>
                      <VisibilityOutlinedIcon sx={{ fontSize: {xs:'8px !important', lg:'9px !important'}, color: '#1565C0' }} />
                      <Typography sx={{ fontSize: {xs:'8px', lg:'9px'}, fontFamily: 'Rubik', fontWeight: 600, color: '#1565C0', lineHeight: 1 }}>Internal Rev.</Typography>
                    </Box>
                  </CustomTooltip>
                )}
              </Box>
            )}
          </Box>
          <Box mb={{xs:0.5, md:0.8,  xl:1.3}}  sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, }}>
            <Typography  sx={{color:'#878887' , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Event Type</Typography>
            <Typography sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>{projectReducer?.project_details?.event_type?.name}</Typography>
          </Box>
          <Box mb={{xs:0.5,md:0.8, xl:1.3}}  sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, }}>
            <Typography  sx={{color:'#878887' , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Editing Start Date</Typography>
            <Typography sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>{timeStampToDayString(projectReducer?.project_details?.editor_start_date)}, {timeStampToFormatTimeZone(projectReducer?.project_details?.editor_start_date)}</Typography>
          </Box>

          {/* ── Due Date section ── */}
          <Box mb={{xs:0.5,md:0.8, xl:1.3}} sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}}}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography sx={{ color: '#878887', fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily: 'Rubik' }}>Due Date</Typography>
              {isAdmin && (
                <CustomTooltip title={dueDate ? 'Edit due date' : 'Set due date'}>
                  <EditCalendarOutlinedIcon
                    onClick={openDueDatePopover}
                    sx={{ fontSize: {xs:'9px !important',md:'9px !important',lg:'11px !important',xl:'16px !important'}, color: '#08231B', cursor: 'pointer' }}
                  />
                </CustomTooltip>
              )}
            </Box>
            {dueDateStr ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                {overdue ? (
                  <EventBusyOutlinedIcon sx={{ fontSize: {xs:'9px',lg:'11px'}, color: '#e53935' }} />
                ) : (
                  <EventAvailableOutlinedIcon sx={{ fontSize: {xs:'9px',lg:'11px'}, color: countdown ? countdownColor : '#555' }} />
                )}
                <Typography sx={{ fontWeight: theme.typography.fontWeightBold, fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily: 'Rubik', color: overdue ? '#e53935' : countdown ? countdownColor : 'inherit' }}>
                  {dueDateStr}
                </Typography>
                {countdown && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', ml: 0.5 }}>
                    <TimerOutlinedIcon sx={{ fontSize: {xs:'9px',lg:'11px'}, color: countdownColor }} />
                    <Typography sx={{ fontSize: {xs:'9px',md:'9px',lg:'10px',xl:'14px'}, fontFamily: 'Rubik', fontWeight: 700, color: countdownColor }}>
                      {countdown.overdue
                        ? 'Overdue!'
                        : countdown.d > 0
                          ? `${countdown.d}d ${String(countdown.h).padStart(2,'0')}:${String(countdown.m).padStart(2,'0')}:${String(countdown.s).padStart(2,'0')}`
                          : `${String(countdown.h).padStart(2,'0')}:${String(countdown.m).padStart(2,'0')}:${String(countdown.s).padStart(2,'0')}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontWeight: theme.typography.fontWeightBold, fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily: 'Rubik', color: '#aaa', fontStyle: 'italic' }}>
                {isAdmin ? 'Not set' : 'Not set'}
              </Typography>
            )}
          </Box>

          {/* Due date custom picker */}
          {Boolean(dueDateAnchor) && (
            <Popper open anchorEl={dueDateAnchor} placement="bottom-start" style={{ zIndex: 1300 }}>
              <ClickAwayListener onClickAway={closeDueDatePopover}>
                <Box>
                  <DateTimePicker
                    extendEditingDate={true}
                    placeholder="Select due date & time"
                    defaultValue={dueDatePickerDate || new Date()}
                    dateTime={dueDatePickerDate || new Date()}
                    setDateTime={setDueDatePickerDate}
                    onDateChange={(seconds) => setDueDatePickerDate(new Date(seconds * 1000))}
                    buttonClickHandler={saveDueDateHandler}
                    buttonDisabled={savingDueDate}
                    medium={true}
                  />
                </Box>
              </ClickAwayListener>
            </Popper>
          )}

          {(() => {
            const editorMembers = (projectReducer?.org_project_members_list ?? []).filter(
              m => m?.role_name === 'Editor' || m?.role?.name === 'Editor'
            );
            if (editorMembers.length === 0) return null;
            return (
              <Box mb={{xs:0.5,md:0.8, xl:1.3}} sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw',lg:'2.3vw', xl:'2.9vw'}}}>
                <Typography sx={{color:'#878887', fontSize:{xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Editor(s)</Typography>
                {editorMembers.map((member, idx) => (
                  <Box key={idx} sx={{display:'flex', alignItems:'center', gap:'6px', mt: idx > 0 ? 0.5 : 0}}>
                    <Typography sx={{fontWeight:theme.typography.fontWeightBold, fontSize:{xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>
                      {member?.name}
                    </Typography>
                    <AutoAssignedBadge autoAssigned={member?.auto_assigned} />
                  </Box>
                ))}
              </Box>
            );
          })()}

         {(projectReducer?.project_details?.song_type || projectReducer?.project_details?.event_songs?.length > 0 || projectReducer?.project_details?.description || projectReducer?.project_details?.event_notes?.length > 0)  &&  <Box mb={{xs:0.5,md:0.8, xl:1.3}} mt={{xs:3.5,md:3.8, xl:4.3}}  sx={{paddingLeft:{xs:'0.1vw',sm:'0.1vw',md:'0.8vw' ,lg:'1.5vw', xl:'2.3vw'}, }}>
             <Typography sx={{fontSize:{xs:'16px',lg:'20px',xl:'24px'}, ml:1.5, mb:1}}>Client Notes</Typography>
          </Box>}

          <Box mb={{xs:0.5,md:0.8, xl:1.3}}  sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, }}>

           {projectReducer?.project_details?.song_type   &&
          <Box className="flex a-center" gap={{xs:0.5, xl:1}}>
              <Typography  sx={{color:'#878887' , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>My Music</Typography>
                   <CustomTooltip title={'View music notes.'}  PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [-35, -4],
                                },
                              },
                            ],
                          }} >
                      <InfoOutlinedIcon onClick={()=> dispatch(updateProjectState({song_desc_popup : true}))} sx={{fontSize: {xs:'9px !important',md:'9px !important',lg:'10.67px !important', xl:'16px !important'}, color:'#08231B', cursor:'pointer'}}/>
                  </CustomTooltip>
            </Box>}

            {projectReducer?.project_details?.event_songs?.length > 0 && <Typography onClick={()=> dispatch(updateProjectState({uploaded_songs_popup : true}))} sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik', cursor:'pointer', color:theme.palette.secondary.main}}>Music Files</Typography>}
          </Box>

           <Box mb={{xs:0.5, md:0.8, xl:1.3}}  sx={{paddingLeft:{xs:'1.38vw',sm:'1.6vw',md:'1.8vw' ,lg:'2.3vw', xl:'2.9vw'}, }}>
           {  projectReducer?.project_details?.description  &&
            <Box className="flex a-center" gap={{xs:0.5, xl:1}}>
              <Typography  sx={{color:'#878887' , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik'}}>Notes</Typography>
                  { <CustomTooltip title={'View project notes.'}  PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [-35, -4],
                                },
                              },
                            ],
                          }} >
                      <InfoOutlinedIcon onClick={()=> dispatch(updateProjectState({view_notes_to_editor_popup : true}))} sx={{fontSize: {xs:'9px !important',md:'9px !important',lg:'10.67px !important', xl:'16px !important'}, color:'#08231B', cursor:'pointer'}}/>
                  </CustomTooltip>}
            </Box>}

           { projectReducer?.project_details?.event_notes?.length > 0 &&   <Typography onClick={()=> dispatch(updateProjectState({uploaded_documents_to_editor_popup : true}))} sx={{fontWeight:theme.typography.fontWeightBold , fontSize: {xs:'9px',md:'9px',lg:'10.67px',xl:'16px'}, fontFamily:'Rubik', cursor:'pointer', color:theme.palette.secondary.main}}>My Documents</Typography>}
          </Box>

        </Box>
      </Box>
    </CustomCard>
  )
}

export default EventDetailCard
